import { useCallback } from 'react';

import { ProgressBar } from '../../components/ProgressBar';
import { Skeleton } from '../../components/Skeleton';
import { Pressable } from '../../primitives/Pressable';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { brandColor } from '../../styles/brand-colors';
import { DARK_LIFTED } from '../../styles/elevation';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { restyleKey } from '../../utils/restyleKey';
import type { MaterialListRowProps } from './DeckMaterials.types';
import { DECK_MATERIAL_ICONS } from './icons';

/**
 * One row card of the "All Sources" LIST (Figma `107:1522`) — the same
 * material as a `MaterialTile`, laid out for a list: the tile's cover as a
 * portrait thumbnail at the left, then the kind's glyph and the name, the
 * tile's caption under them, and the ⋮ menu at the right edge. Same press
 * (opens the material), same status line while a document is on its way in,
 * same menu slot — only the shape differs from the tile, so the two views
 * can never disagree about what a material offers.
 *
 * The row says what the tile says and nothing more: `details` is the tile's
 * caption. While the material is uploading, the STATUS takes that line's
 * place, exactly as it takes the tile's caption.
 *
 * A material with no cover shows the EMPTY thumbnail frame, one step down
 * the ramp, as the tile does — the glyph beside the name already says what
 * kind it is.
 *
 * The ⋮ (or, for a failed upload, Retry) sits BESIDE the pressable, not
 * inside it: a button inside a button is invalid HTML, and the browser
 * re-parents it — the same constraint that puts `MaterialTile`'s menu outside
 * its frame. So the row is a flex line of [pressable body][trailing], and the
 * body is what opens the material.
 *
 * The body carries `flex` itself: a web `Pressable` is a `<button>`, which is
 * not a flex container until told so (a `View` is, by its reset). Without it
 * `flex-row` does nothing and the name drops under the thumbnail, out of the
 * row's clipped box.
 *
 * Geometry (`h-[100px]`, the `60×84` thumbnail) is `MATERIAL_LIST_*` in
 * `materials-grid-layout.ts`.
 */
export function MaterialListRow({
  item,
  onOpen,
  actionsSlot,
  preview,
  details,
  status,
  className,
  testID,
}: MaterialListRowProps) {
  const { isDark } = useTheme();
  const Icon = DECK_MATERIAL_ICONS[item.kind];
  const glyphColor = brandColor('secondary', '700', isDark);

  const open = useCallback(() => onOpen(item.ref), [onOpen, item.ref]);

  const trailing =
    actionsSlot ??
    (status?.kind === 'failed' ? (
      <Pressable
        onPress={status.onRetry}
        accessibilityRole="button"
        accessibilityLabel={status.retryLabel}
        className="px-2"
        testID={testID ? `${testID}-retry` : undefined}>
        <Text className="text-b2 font-semibold text-primary-600 native:font-mona-semibold">
          {status.retryLabel}
        </Text>
      </Pressable>
    ) : null);

  return (
    <View
      className={cn(
        'h-[100px] w-full flex-row items-center gap-1 overflow-hidden rounded-[26px] border border-secondary-200 bg-surface-raised pr-2',
        className,
      )}
      testID={testID}>
      <Pressable
        // As the tile: on its way in (or failed), the row does not open; keyed
        // so landing remounts it instead of flipping its dim in place.
        key={restyleKey('frame', !!status)}
        disabled={!!status}
        onPress={open}
        accessibilityRole="button"
        accessibilityLabel={item.label}
        className="flex h-full min-w-0 flex-1 flex-row items-center gap-4 py-2 pl-2"
        testID={testID ? `${testID}-frame` : undefined}>
        <View
          className={cn(
            'relative h-[84px] w-[60px] shrink-0 overflow-hidden rounded-[18px] border border-secondary-200',
            // Inside the row: a preview fills its own sheet; the empty slot —
            // grey in light — is the lifted step in dark, never a dark hole.
            preview ? 'bg-surface-raised' : cn('bg-secondary-100', DARK_LIFTED),
          )}>
          {preview ? <View className="absolute inset-0">{preview}</View> : null}
          {!preview && status?.kind === 'uploading' ? (
            <View className="absolute inset-0">
              <Skeleton className="h-full w-full rounded-none" />
            </View>
          ) : null}
        </View>

        <View className="min-w-0 flex-1 flex-col justify-center gap-0.5">
          <View className="w-full flex-row items-center gap-2">
            {/* The same glyph the tile's badge and the tab wear. */}
            <View className="shrink-0" testID={testID ? `${testID}-kind` : undefined}>
              <Icon color={glyphColor} size={20} />
            </View>
            {/* Explicit ramp on each Text: native does not inherit type through a View. */}
            <Text
              numberOfLines={1}
              className="min-w-0 flex-1 text-h3 font-semibold text-secondary-800 native:font-mona-semibold">
              {item.label}
            </Text>
          </View>
          {status ? (
            <View
              className="h-[22px] w-full flex-row items-center gap-1.5"
              testID={testID ? `${testID}-status` : undefined}>
              {status.kind === 'uploading' ? (
                // The bar alone, as on the tile: real progress, or the
                // travelling segment until the run reports any.
                <ProgressBar
                  live
                  indeterminate={status.progress == null}
                  value={status.progress ?? 0}
                  accessibilityLabel={status.label}
                  testID={testID ? `${testID}-progress` : undefined}
                />
              ) : (
                <Text
                  numberOfLines={1}
                  className="text-b2 font-medium text-red-600 native:font-mona-medium">
                  {status.label}
                </Text>
              )}
            </View>
          ) : details ? (
            <Text
              numberOfLines={1}
              className="w-full text-b2 font-medium text-secondary-400 native:font-mona-medium">
              {details}
            </Text>
          ) : null}
        </View>
      </Pressable>

      {trailing ? <View className="shrink-0">{trailing}</View> : null}
    </View>
  );
}
(MaterialListRow as { displayName?: string }).displayName = 'MaterialListRow';
